/* eslint-disable @typescript-eslint/member-ordering */
import {Injectable, isDevMode, NgZone} from '@angular/core';
import {fromEvent, merge, Observable, Subscription, timer} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {
  InactivityModalComponent,
} from '../../shared-modules/shared-module/components/inactivity-modal/inactivity-modal.component';
import {Auth, signOut} from '@angular/fire/auth';
import {ModalControllerService} from '../../shared/shared-services/overlay/modal-controller.service';

// 1 * 1000 * 60 * 20 = 1,200,000 ms (20 minutes)
const TIMEOUT = 1_200_000 as const;

@Injectable({providedIn: 'root'})
export class InactivityService {
  private events$: Observable<Event>;
  private inactivitySubscription?: Subscription;
  private subscriptions: Record<string, Subscription> = {};

  constructor(
    private readonly auth: Auth,
    private readonly ngZone: NgZone,
    private readonly modalController: ModalControllerService,
  ) {
    this.events$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'touchstart'),
      fromEvent(window, 'load'),
    );
  }

  startWatching(): void {
    this.ngZone.runOutsideAngular(() => {
      this.inactivitySubscription?.unsubscribe();
      this.inactivitySubscription = this.events$
        .pipe(switchMap(() => timer(TIMEOUT).pipe(takeUntil(this.events$))))
        .subscribe(async () => {
          const mc = await this.modalController.create({
            component: InactivityModalComponent,
            cssClass: ['refresh-modal'],
            backdropDismiss: false,
          });
          await mc.present();

          this.unsubscribeAll();

          if (!isDevMode()) {
            await signOut(this.auth).catch(console.error);
          }
        });

    });
  }

  unsubscribeAll(): void {
    this.inactivitySubscription?.unsubscribe();
    this.inactivitySubscription = undefined;

    for (const subId in this.subscriptions) {
      if (Object.prototype.hasOwnProperty.call(this.subscriptions, subId)) {
        this.subscriptions[subId].unsubscribe();
      }
    }
    this.subscriptions = {};
  }

  addSubscription(subscription: Subscription | undefined, subId: string): void {
    if (!subscription) return;
    if (this.subscriptions[subId]) {
      console.error(`Cannot add Subscription to inactivity.service. Id '${subId}' already exists.`);
      return;
    }
    this.subscriptions[subId] = subscription;
  }
}
